import React, { useCallback, useMemo } from 'react'

import { ComponentList, IntersectionObserver, ScrollHere } from '@vividseats/vivid-ui-kit'
import { WithId } from '@vividseats/vivid-ui-kit/dist/components/component-list'
import { IntersectionObserverProps } from '@vividseats/vivid-ui-kit/dist/components/intersection-observer'
import { ScrollHereProps } from '@vividseats/vivid-ui-kit/dist/components/scroll-here'

const scrollHereProps: TypedOmit<ScrollHereProps, 'condition'> = {
    scrollOptions: {
        block: 'start',
        inline: 'nearest',
    },
}

const DEFAULT_ROOT_MARGIN = '250px'

export interface InfiniteScrollProps<TItem, TItemComponentProps extends WithId> {
    readonly isFetching: boolean
    readonly items: TItem[]
    readonly totalItems: number
    readonly loadMoreHandler: () => void
    readonly ItemComponent: React.FC<TItemComponentProps>
    readonly itemToItemComponentProps: (item: TItem, index: number) => TItemComponentProps
    readonly intersectionObserverProps?: TypedOmit<IntersectionObserverProps, 'onIntersection'>
}

const InfiniteScroll: <TItem, TItemComponentProps extends WithId>(
    props: InfiniteScrollProps<TItem, TItemComponentProps>,
) => React.ReactElement<InfiniteScrollProps<TItem, TItemComponentProps>> | null = ({
    isFetching,
    items,
    totalItems,
    loadMoreHandler,
    ItemComponent,
    itemToItemComponentProps,
    intersectionObserverProps,
}) => {
    const hasMoreItems: boolean = useMemo(() => totalItems > items.length, [totalItems, items.length])

    const loadMoreHandlerWithCheck = useCallback(
        (shouldFetch: boolean) => {
            if (shouldFetch && !isFetching) {
                loadMoreHandler()
            }
        },
        [isFetching, loadMoreHandler],
    )

    const itemComponentProps = useMemo(() => items.map(itemToItemComponentProps), [itemToItemComponentProps, items])

    return totalItems > 0 ? (
        <>
            {/* Page will be scrolled to the start when 'Show Less' button is clicked */}
            <ScrollHere {...scrollHereProps} condition={false} />
            <>
                {/* Render lazy loaded items */}
                <ComponentList Component={ItemComponent} componentPropsList={itemComponentProps} />

                {/* Use intersection observer if there are remaining items that haven't been lazy loaded yet */}
                {!isFetching && hasMoreItems && (
                    <IntersectionObserver
                        {...intersectionObserverProps}
                        onIntersection={loadMoreHandlerWithCheck}
                        intersectionOptions={{
                            rootMargin: DEFAULT_ROOT_MARGIN,
                            ...intersectionObserverProps?.intersectionOptions,
                        }}
                        style={{ height: '10px' }}
                    />
                )}
            </>
        </>
    ) : null
}

export default InfiniteScroll
